/**
 * Central investment / onboarding eligibility rules.
 *
 * Regular / Free (isSubscriptionFree): KYC + onboarding only.
 * Direct (paid): KYC + onboarding + risk assessment + payment + Digio agreement.
 * Prime / VIP and other non-Direct types: KYC + onboarding only (existing non-Direct path).
 *
 * Uses existing onboarding-status fields. Do not invent new API keys.
 */

const DIRECT_TYPE = 'DIRECT';

const asObject = (value) =>
  value && typeof value === 'object' ? value : null;

export const getOnboardingStatusPayload = (onboarding) => {
  if (!asObject(onboarding)) return {};

  return (
    asObject(onboarding.onboardingStatus) ||
    asObject(onboarding.data) ||
    asObject(onboarding.result) ||
    onboarding
  );
};

const readSources = (onboarding) => {
  const status = getOnboardingStatusPayload(onboarding);
  const nested = asObject(status?.data);
  return [onboarding, status, nested].filter(Boolean);
};

const readFlag = (onboarding, key) =>
  readSources(onboarding).some((source) => source?.[key] === true);

const readNestedCompleted = (onboarding, keys) =>
  readSources(onboarding).some((source) =>
    keys.some((key) => source?.[key]?.completed === true)
  );

export const getCustomerType = (onboarding) => {
  for (const source of readSources(onboarding)) {
    const type = source?.customerType || source?.customer_type;
    if (type) return type;
  }
  return null;
};

export const isSubscriptionFree = (onboarding) =>
  readFlag(onboarding, 'isSubscriptionFree');

export const isDirectCustomer = (onboarding) =>
  getCustomerType(onboarding) === DIRECT_TYPE;

export const isOnboardingComplete = (onboarding) =>
  readFlag(onboarding, 'isOnboardingCompleted') &&
  readFlag(onboarding, 'isUCCOnboardingCompleted');

/** BSE/exchange-side registration. Survives a later KRA re-review, unlike isOnboardingCompleted. */
export const isUccOnboardingComplete = (onboarding) =>
  readFlag(onboarding, 'isUCCOnboardingCompleted');

/**
 * Existing investors keep transacting on folios they already hold.
 * A KYC/KRA re-review months or years after onboarding flips isOnboardingCompleted
 * back to false while the BSE UCC stays valid — that must not block
 * invest / redeem / switch / STP / SWP on a folio the customer already holds.
 */
export const canTransactAsExistingInvestor = (
  onboarding,
  { hasExistingHolding } = {},
) => hasExistingHolding === true && isUccOnboardingComplete(onboarding);

export const isRiskAssessmentComplete = (onboarding) =>
  readNestedCompleted(onboarding, ['riskAssessment', 'risk_assessment']);

/** Existing Digio completion field from onboarding-status: agreement.completed */
export const isAgreementComplete = (onboarding) =>
  readNestedCompleted(onboarding, ['agreement']);

export const isKycStepDone = (onboarding) =>
  readSources(onboarding).some(
    (source) =>
      source?.kyc?.completed === true || source?.kyc?.inReview === true,
  );

export const isBankLinkingComplete = (onboarding) =>
  readNestedCompleted(onboarding, ['bankLinking', 'bank_linking']);

export const isNomineeComplete = (onboarding) =>
  readNestedCompleted(onboarding, ['nominee']);

/** KYC with KRA; bank + nominee done — user cannot action anything yet. */
export const isKycInReview = (onboarding) =>
  readSources(onboarding).some(
    (source) =>
      source?.kyc?.inReview === true &&
      source?.kyc?.completed !== true &&
      source?.bankLinking?.completed === true &&
      source?.nominee?.completed === true,
  );

/**
 * Direct extras (payment, Digio, mandatory RA) apply only to paid Direct customers.
 * Free subscription and Prime/VIP must not inherit Direct requirements.
 */
export const isDirectJourneyRequired = (onboarding) =>
  isDirectCustomer(onboarding) && !isSubscriptionFree(onboarding);

export const getOnboardingIncompleteSteps = (onboarding) => {
  const steps = [];
  if (!readFlag(onboarding, 'isOnboardingCompleted')) {
    steps.push('Onboarding');
  }
  if (!readFlag(onboarding, 'isUCCOnboardingCompleted')) {
    steps.push('UCC Onboarding');
  }
  return steps;
};

export const getInvestmentEligibility = (
  onboarding,
  { isSubscriptionActive, hasExistingHolding } = {},
) => {
  const customerType = getCustomerType(onboarding);
  const isFreeCustomer = isSubscriptionFree(onboarding);
  const isDirect = isDirectCustomer(onboarding);
  const existingInvestorBypass = canTransactAsExistingInvestor(onboarding, {
    hasExistingHolding,
  });
  const onboardingDone =
    isOnboardingComplete(onboarding) || existingInvestorBypass;
  const riskDone = isRiskAssessmentComplete(onboarding);
  const agreementDone = isAgreementComplete(onboarding);
  const directJourney = isDirect && !isFreeCustomer;
  const paid = isSubscriptionActive === true || isFreeCustomer;

  let nextAction = null;
  if (!onboardingDone) {
    nextAction = 'onboarding';
  } else if (directJourney && !riskDone) {
    nextAction = 'risk';
  } else if (directJourney && !paid) {
    nextAction = 'payment';
  } else if (directJourney && !agreementDone) {
    nextAction = 'agreement';
  }

  return {
    customerType,
    isFreeCustomer,
    isDirect,
    isOnboardingComplete: onboardingDone,
    isExistingInvestorBypass: existingInvestorBypass,
    isRiskAssessmentComplete: riskDone,
    isAgreementComplete: agreementDone,
    isRiskAssessmentMandatory: directJourney,
    isAgreementMandatory: directJourney,
    shouldSkipPlanSelection: isFreeCustomer,
    shouldSkipPayment: isFreeCustomer || !isDirect,
    shouldSkipAgreement: !directJourney,
    canInvest: onboardingDone && nextAction == null,
    nextAction,
  };
};

/** Normalize RTK / lazy-query onboarding responses to the status payload object. */
export const extractOnboardingStatusFromResponse = (response) => {
  if (!response) return null;

  const payload = getOnboardingStatusPayload(response);
  const hasStatusFields =
    payload?.customerType != null ||
    payload?.isOnboardingCompleted != null ||
    payload?.kyc != null;

  return hasStatusFields ? payload : null;
};

/**
 * Invest-click gate — paid Direct order:
 * onboarding → risk → payment → agreement.
 */
export const getInvestGateResult = (
  onboarding,
  { isSubscriptionActive, hasExistingHolding } = {},
) => {
  const eligibility = getInvestmentEligibility(onboarding, {
    isSubscriptionActive,
    hasExistingHolding,
  });

  if (!eligibility.isOnboardingComplete) {
    return {
      allowed: false,
      action: 'onboarding',
      incompleteSteps: getOnboardingIncompleteSteps(onboarding),
      eligibility,
    };
  }

  if (eligibility.nextAction === 'risk') {
    return { allowed: false, action: 'risk', eligibility };
  }

  if (eligibility.nextAction === 'payment') {
    return { allowed: false, action: 'payment', eligibility };
  }

  if (eligibility.nextAction === 'agreement') {
    return { allowed: false, action: 'agreement', eligibility };
  }

  return { allowed: true, action: null, eligibility };
};

/**
 * Whether Direct-plan payment must wait on full onboarding.
 * Applies to Regular (upgrading), Direct (plan chosen, payment pending), and
 * users who have not yet been assigned a customer type but select Direct.
 * Complimentary Direct (isSubscriptionFree) is excluded.
 */
export const shouldRequireOnboardingForDirectPayment = (onboarding) => {
  if (isSubscriptionFree(onboarding)) {
    return false;
  }

  const customerType = getCustomerType(onboarding);
  return (
    customerType === 'REGULAR' ||
    customerType === 'DIRECT' ||
    customerType == null
  );
};

/**
 * Direct-plan payment gate — onboarding and risk must be complete before payment
 * for Regular and paid Direct customers.
 */
export const getUpgradeGateResult = (onboarding) => {
  if (!shouldRequireOnboardingForDirectPayment(onboarding)) {
    return { allowed: true, action: null };
  }

  if (!isOnboardingComplete(onboarding)) {
    if (isKycInReview(onboarding)) {
      return { allowed: false, action: 'kycInReview' };
    }

    if (isKycStepDone(onboarding)) {
      return {
        allowed: false,
        action: 'onboarding',
        incompleteSteps: getOnboardingIncompleteSteps(onboarding),
      };
    }

    return { allowed: false, action: 'kyc' };
  }

  if (isDirectJourneyRequired(onboarding) && !isRiskAssessmentComplete(onboarding)) {
    return { allowed: false, action: 'risk' };
  }

  return { allowed: true, action: null };
};
